import { useEffect, useCallback } from "react";
import { navigate } from "@reach/router";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { RFX_TABS } from "utils/constants";
import { useEvaluation } from "../hooks/useEvaluation";
import { InitialEvaluationProvider } from "../hooks/InitialEvaluationContext";
import { RFxEvaluationSteps } from "../components/RFxEvaluationSteps";
import type { PanelMode } from "../types/panel.type";

interface EvaluationPageProps {
	evaluationId?: string;
	path?: string;
}

export const EvaluationPage: React.FC<EvaluationPageProps> = () => {
	const { rfxData } = useRfxData();
	const rfxId = rfxData?.id;
	const { clientPaths } = useAuthContext();
	const hasAIAccess = useHasAIEvaluationAccess();

	const { isLoading, evaluationState, currentUserContext, fetchEvaluationState, updateProgress } = useEvaluation({
		rfxId,
	});

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleBack = (): void => {
		navigate(clientPaths.viewRfx(rfxData, RFX_TABS.EVALUATION));
	};

	const handleStepChange = useCallback(
		(step: number) => {
			// Only persist when moving forward — going back is a UI-only action.
			// Decrementing current_step would break backend guards that gate
			// scoring endpoints on current_step >= 2.
			const highestStep = evaluationState?.currentStep ?? 0;
			if (step > highestStep) {
				updateProgress({ current_step: step });
			}
		},
		[updateProgress, evaluationState?.currentStep]
	);

	const handleAdvanceStep = useCallback(
		async (step: number): Promise<void> => {
			const highestStep = evaluationState?.currentStep ?? 0;
			if (step > highestStep) {
				await updateProgress({ current_step: step });
			}
		},
		[updateProgress, evaluationState?.currentStep]
	);

	const handlePanelModeChange = useCallback(
		(mode: PanelMode) => {
			updateProgress({ panel_mode: mode });
		},
		[updateProgress]
	);

	if (isLoading) {
		return (
			<div className="space-y-3 p-6">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	return (
		<InitialEvaluationProvider rfxId={rfxId}>
			<div data-testid="evaluation-page" className="p-6">
				<div className="mb-4 flex flex-row items-center">
					<Button btntype="secondary" onClick={handleBack}>
						← Back to RFx
					</Button>
					<div className="ml-4 flex flex-col">
						<div>
							RFx: {rfxData?.name}
						</div>
						{rfxData?.project && <div>
							Project: {rfxData?.project?.name}
						</div>}
					</div>
				</div>
				<div className="mb-6">
					<h1 className="text-2xl font-semibold text-gray-900">
						{hasAIAccess ? (
							<>EvaluA<span className="text-utility-brand-700">I</span>tion tool</>
						) : (
							"Evaluation"
						)}
					</h1>
					<p className="mt-1 text-sm text-gray-500">
						{hasAIAccess
							? "Use AI-assisted evaluation to systematically score and compare supplier submissions."
							: "Systematically score and compare supplier submissions."}
					</p>
				</div>
				<RFxEvaluationSteps
					rfxData={rfxData}
					evaluationState={evaluationState}
					isRfxLead={currentUserContext?.isRfxLead ?? false}
					onComplete={handleBack}
					onStepChange={handleStepChange}
					onPanelModeChange={handlePanelModeChange}
					onAdvanceStep={handleAdvanceStep}
				/>
			</div>
		</InitialEvaluationProvider>
	);
};
